import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import axiosInstance from "../api/axiosInstance";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { Dialog } from "@capacitor/dialog";
import { jwtDecode } from "jwt-decode";
import { FavoriteBorderRounded, FavoriteRounded } from "@mui/icons-material";
import ReportDialog from "../components/ReportDialog";
import { CircularProgress } from '@mui/material';
import "../styles/comments.css";

const TrippinComments = ({ trippinPostId, placeName, onClose, id }) => {
  const navigate = useNavigate();

  const longPressTimer = useRef(null);
  const startPosition = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const isIos = Capacitor.getPlatform() === 'ios';
  const [isClosing, setIsClosing] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportEntityId, setReportEntityId] = useState(null);
  const [reportEntityType, setReportEntityType] = useState("Trippin_comment");

  const openReportDialog = (type, entityId) => {
    setReportEntityType(type); // 'comment' or 'reply'
    setReportEntityId(entityId);
    setIsReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setIsReportDialogOpen(false);
    setReportEntityId(null);
    setReportEntityType("comment");
  };

  const fetchComments = async () => {
    if (!trippinPostId || !placeName) return;
  
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/trippins/comments/${trippinPostId}/place/${placeName}`
      );
      if (response.data?.comments) {
        // Map the comments and include the 'isLiked' property
        setComments(
          response.data.comments.map((comment) => ({
            ...comment,
            likesCount: comment.likes_count, // Map likes_count to likesCount
            isLiked: comment.isLiked || false, // Ensure 'isLiked' is set
          }))
        );
      }
    } catch (err) {
      console.error('Error fetching Trippin comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleLikeComment = async (commentId, idx) => {
    
     // Get the specific comment using the index (idx)
     const comment = comments[idx];
     const currentlyLiked = comment.isLiked; // Define this properly here
 
  
    try {
        // Optimistic UI update
      setComments((prev) => {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          isLiked: !currentlyLiked, // Use the correct variable here
          likesCount: currentlyLiked
            ? Number(updated[idx].likesCount) - 1
            : Number(updated[idx].likesCount) + 1,
        };
        return updated;
      });
  
      // Backend operation
      if (currentlyLiked) {
        await axiosInstance.delete(`/trippins/comments/${commentId}/unlike`);
      }
      else {
        await axiosInstance.post(`/trippins/comments/${commentId}/like`);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
  
      // Revert UI on failure
      setComments((prev) => {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          isLiked: currentlyLiked, // Revert to original state
          likesCount: currentlyLiked
            ? Number(updated[idx].likesCount) + 1
            : Number(updated[idx].likesCount) - 1,
        };
        return updated;
      });
    }
  };
  
  const toggleLikeReply = async (replyId, commentIdx, replyIdx) => {
    // Get the specific reply
    const reply = comments[commentIdx].replies[replyIdx];
    const currentlyLiked = reply.isLiked;
  
    // Use a flag to track if the state update is redundant
    let updateFlag = false;
  
    try {
      // Optimistic UI update
      setComments((prev) => {
        if (updateFlag) return prev; // Prevent redundant updates
        updateFlag = true; // Set the flag to true after the first update
  
        const updated = [...prev];
        const targetReply = updated[commentIdx].replies[replyIdx];
        targetReply.isLiked = !currentlyLiked;
        targetReply.likesCount = currentlyLiked
          ? Number(targetReply.likesCount) - 1
          : Number(targetReply.likesCount) + 1;
        return updated;
      });
  
      // Backend operation
      if (currentlyLiked) {
        await axiosInstance.delete(`/trippins/replies/${replyId}/unlike`);
      }
      else {
        await axiosInstance.post(`/trippins/replies/${replyId}/like`);
      }
    } catch (error) {
      console.error("Error toggling like for reply:", error);
  
      // Revert UI on failure
      setComments((prev) => {
        const updated = [...prev];
        const targetReply = updated[commentIdx].replies[replyIdx];
        targetReply.isLiked = currentlyLiked;
        targetReply.likesCount = currentlyLiked
          ? Number(targetReply.likesCount) + 1
          : Number(targetReply.likesCount) - 1;
        return updated;
      });
    }
  };
  
  const fetchRepliesForComment = async (commentId, offset = 0, limit = 4) => {
    try {
      const response = await axiosInstance.get(
        `/trippins/comments/${commentId}/replies`,
        { params: { offset, limit } }
      );
  
      const replies = response.data.replies.map((reply) => ({
        ...reply,
        likesCount: Number(reply.likesCount || 0),
      }));
  
      return {
        replies,
        totalReplies: response.data.totalReplies || 0,
      };
    } catch (err) {
      console.error("Error fetching replies:", err);
      return { replies: [], totalReplies: 0 };
    }
  };
  
  const postCommentOrReply = async () => {
    try {
      if (replyTo) {
        await axiosInstance.post("/trippins/replies", {
          comment_id: replyTo.comment_id,
          reply_text: newComment,
        });
      }
      else {
        if (!id) {
          console.error("No post_place_id available to post a comment.");
          return;
        }
        await axiosInstance.post("/trippins/comments", {
          post_place_id: id,
          comment_text: newComment,
        });
      }
      setNewComment("");
      setReplyTo(null);
      fetchComments();
    } catch (err) {
      console.error("Error posting comment or reply:", err);
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setNewComment(`@${comment.username} `);
  };

  const resetReply = () => {
    setReplyTo(null);
    setNewComment("");
  };

  const toggleReplies = async (comment, idx) => {
    if (!comment.showReplies) {
      // If currently hidden, fetch & show the first 4
      const { replies, totalReplies } = await fetchRepliesForComment(
        comment.comment_id,
        0,
        4
      );
      setComments((prev) => {
        const updated = [...prev];
        updated[idx] = {
          ...comment,
          showReplies: true,
          replies,
          totalReplies,
          loadedRepliesCount: replies.length,
        };
        return updated;
      });
    }
    else {
      setComments((prev) => {
        const updated = [...prev];
        updated[idx] = { ...comment, showReplies: false };
        return updated;
      });
    }
  };

  const loadMoreReplies = async (comment, idx) => {
    const newOffset = comment.loadedRepliesCount || 0;
    const { replies: newReplies } = await fetchRepliesForComment(
      comment.comment_id,
      newOffset,
      4
    );
    setComments((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...comment,
        replies: [...(comment.replies || []), ...newReplies],
        loadedRepliesCount: newOffset + newReplies.length,
      };
      return updated;
    });
  };

  const handleLongPress = async (item, type, commentIdx, replyIdx = null) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    let decodedToken;
    try {
      decodedToken = jwtDecode(accessToken);
    } catch (error) {
      console.error("Error decoding token:", error);
      return;
    }
    const userId = decodedToken.user_id;
    
    if (item.user_id !== userId) {
      if (type === "comment") {
        openReportDialog("comment", item.comment_id);
      }
      else if (type === "reply") {
        openReportDialog("reply", item.reply_id);
      }
    }
    else {
      if (replyIdx !== null) {
        const { value } = await Dialog.confirm({
          title: "Delete Reply",
          message: "Are you sure you want to delete this reply?",
        });
  
        if (value) {
          try {
            await axiosInstance.delete(`/trippins/replies/${item.reply_id}`);
            setComments((prev) => {
              const updated = [...prev];
              const currentReplies = updated[commentIdx].replies || [];
              updated[commentIdx].replies = currentReplies.filter((_, i) => i !== replyIdx);
              return updated;
            });
          } catch (err) {
            console.error("Error deleting reply:", err);
          }
        }
        return;
      }
  
      const { value } = await Dialog.confirm({
        title: "Delete Comment",
        message: "Are you sure you want to delete this comment?",
      });
  
      if (value) {
        try {
          await axiosInstance.delete(`/trippins/comments/${item.comment_id}`);
          setComments((prev) => prev.filter((_, i) => i !== commentIdx));
        } catch (err) {
          console.error("Error deleting comment:", err);
        }
      }
    }
  };

  const handleMouseDown = (item, type, commentIdx, replyIdx = null, event) => {
    event.stopPropagation();
    
    const clientX = event.clientX || event.touches?.[0]?.clientX;
    const clientY = event.clientY || event.touches?.[0]?.clientY;
  
    startPosition.current = { x: clientX, y: clientY };
    hasMoved.current = false;
  
    longPressTimer.current = setTimeout(() => {
      if (!hasMoved.current) {
        handleLongPress(item, type, commentIdx, replyIdx);
      }
    }, 800);
  };
  
  const handleMouseMove = (event) => {
    if (!longPressTimer.current) return;
  
    const clientX = event.clientX || event.touches?.[0]?.clientX;
    const clientY = event.clientY || event.touches?.[0]?.clientY;
  
    const deltaX = Math.abs(clientX - startPosition.current.x);
    const deltaY = Math.abs(clientY - startPosition.current.y);
  
    if (deltaX > 1 || deltaY > 1) {
      hasMoved.current = true;
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  
  const handleMouseUp = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  const getTimeAgo = (utcTimestamp) => {
    const utcDate = fromZonedTime(new Date(utcTimestamp), "UTC");
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = toZonedTime(utcDate, userTimeZone);
    const secondsAgo = Math.floor((new Date() - localTime) / 1000);

    if (secondsAgo < 60) return `${secondsAgo}s`;
    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) return `${minutesAgo}min`;
    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) return `${hoursAgo}h`;
    const daysAgo = Math.floor(hoursAgo / 24);
    if (daysAgo < 7) return `${daysAgo}d`;
    const weeksAgo = Math.floor(daysAgo / 7);
    if (weeksAgo < 4) return `${weeksAgo}w`;
    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) return `${monthsAgo}m`;
    const yearsAgo = Math.floor(daysAgo / 365);
    return `${yearsAgo}y`;
  };

  useEffect(() => {
    if (trippinPostId && placeName) {
      fetchComments();
    }
  }, [trippinPostId, placeName]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div className="comments-overlay" onClick={handleClose}>
      <div
        className={`comments-container ${isClosing ? "close" : "open"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <ReportDialog
          open={isReportDialogOpen}
          onClose={closeReportDialog}
          reportedEntityId={reportEntityId}
          entityType={reportEntityType}
        />
        <div className="comments-header">
          <h3 className="comments-title">Comments</h3>
          <IconButton className="icon-button" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Comments List */}
        <div className="comments-list">
          {loading && (
            <div className="loading-container-comments">
              <CircularProgress size={40} sx={{ color: '#888' }} />
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div className="loading-container" style={{ height: "unset" }}>
              <p style={{ fontSize: "18px", color: "#555", textAlign: "center", marginTop: "50px" }}>
                No comments yet. Be the first to comment
              </p>
            </div>
          )}
          {comments.map((comment, cIdx) => (
            <div
              key={comment.comment_id}
              className="comment-container"
              onMouseDown={(e) => handleMouseDown(comment, "comment", cIdx, null, e)}
              onMouseUp={() => handleMouseUp(comment)}
              onMouseMove={handleMouseMove}
              onTouchStart={(e) => handleMouseDown(comment, "comment", cIdx, null, e)}
              onTouchEnd={() => handleMouseUp(comment)}
              onTouchMove={handleMouseMove}
            >
              <div className="comment-header" onClick={() => navigate(`/profile/${comment.user_id}`)}>
                <img src={comment.profile_url || "/assets/images/default_profile.png"} alt={comment.username} className="comment-profile-pic" />
                <span className="comment-username">{comment.username}</span>
                <span className="comment-time">{getTimeAgo(comment.created_at)}</span>
              </div>

              <div className="comment-text">{comment.comment_text}</div>

              <div className="comment-actions">
                <span onClick={() => handleReply(comment)} className="reply-button">Reply</span>
                <div className="like-section">
                  <div onClick={() => toggleLikeComment(comment.comment_id, cIdx)} className="like-icon">
                    {comment.isLiked ? <FavoriteRounded style={{ color: "#FF3040" }}/> : <FavoriteBorderRounded />}   
                  </div>
                  <span className="like-count">{comment.likesCount || 0}</span>
                </div>
              </div>
  
              {/* Replies */}
              {comment.reply_count > 0 && (
                <div onClick={() => toggleReplies(comment, cIdx)} className="replies-toggle-button">
                  {comment.showReplies ? (
                    <strong>Hide</strong>
                  ) : (
                    comment.reply_count > 1 ? `— View (${comment.reply_count}) replies` : `— View (${comment.reply_count}) reply`
                  )}
                </div>
              )}
  
              {comment.showReplies && comment.replies && (
                <div className="replies-section">
                  {comment.replies.map((reply, rIdx) => (
                    <div
                      key={reply.reply_id}
                      className="reply-container"
                      onMouseDown={(e) => handleMouseDown(reply, "reply", cIdx, rIdx, e)}
                      onMouseUp={(e) => { handleMouseUp(reply); e.stopPropagation(); }}
                      onMouseMove={handleMouseMove}
                      onTouchStart={(e) => handleMouseDown(reply, "reply", cIdx, rIdx, e)}
                      onTouchEnd={(e) => { handleMouseUp(reply); e.stopPropagation(); }}
                      onTouchMove={handleMouseMove}
                    >
                      <div className="comment-header" onClick={() => navigate(`/profile/${reply.user_id}`)}>
                        <img src={reply.profile_url || "/assets/images/default_profile.png"} alt={reply.username} className="comment-profile-pic" />
                        <span className="comment-username">{reply.username}</span>
                        <span className="comment-time">{getTimeAgo(reply.created_at)}</span>
                      </div>
                      
                      <div className="comment-actions">
                        <div className="comment-text">{reply.reply_text}</div>
                        <div className="like-section">
                          <div onClick={() => toggleLikeReply(reply.reply_id, cIdx, rIdx)} className="like-icon">
                            {reply.isLiked ? <FavoriteRounded style={{ color: "#FF3040" }}/> : <FavoriteBorderRounded />}   
                          </div>
                          <span className="like-count">{reply.likesCount || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {comment.showReplies && comment.loadedRepliesCount < comment.totalReplies && (
                    <div 
                      onClick={() => loadMoreReplies(comment, cIdx)} 
                      className="replies-toggle-button"
                      style={{ fontSize: "14px", fontWeight: "bold" }}
                    >
                      - View more ({comment.totalReplies - comment.loadedRepliesCount}) replies
                    </div>    
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
  
        {replyTo && (
          <div className="reply-to-container">
            <span className="reply-to-text">Reply to: @{replyTo.username}</span>
            <IconButton onClick={resetReply} size="small" className="close-reply-button">
              <CloseIcon />
            </IconButton>
          </div>
        )}
        <div
          className="comment-input-section"
          style={isIos ? { marginBottom: '20px' } : {}}
        >
          <input
            type="text"
            placeholder="Add comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="comment-input"
          />
          <button
            onClick={postCommentOrReply}
            className="post-comment-button"
            disabled={!newComment.trim()}
          >
            <SendIcon sx={{ width: 20, height: 20 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrippinComments;